import { cn } from "utils/helpers";
import type { OverallSupplierScore } from "../../types/meeting.type";

export type AwardStatus = "pending" | "awarded" | "unsuccessful";

interface SupplierAwardCardProps {
  supplier: OverallSupplierScore;
  awardStatus: AwardStatus;
  comment: string;
  isLeader: boolean;
  isBelowThreshold: boolean;
  showAwardControls: boolean;
  onAwardStatusChange: (inviteId: number, status: AwardStatus) => void;
  onCommentChange: (inviteId: number, comment: string) => void;
}

const formatScore = (score: number | null): string => {
  if (score === null) {
    return "—";
  }
  return score.toFixed(2);
};

const DiffDisplay: React.FC<{ diff: number | null }> = ({ diff }) => {
  if (diff === null) {
    return <span className="text-gray-400 text-sm tabular-nums">—</span>;
  }

  if (diff === 0) {
    return (
      <span className="text-gray-500 text-sm tabular-nums font-medium">
        ±0.00
      </span>
    );
  }

  const isPositive = diff > 0;
  return (
    <span
      className={cn(
        "text-sm tabular-nums font-medium",
        isPositive ? "text-green-600" : "text-red-600"
      )}
      data-testid="diff-display"
      data-positive={isPositive}
    >
      {isPositive ? "+" : ""}
      {diff.toFixed(2)}
    </span>
  );
};

const AWARD_STATUS_OPTIONS: { value: AwardStatus; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "awarded", label: "Awarded" },
  { value: "unsuccessful", label: "Unsuccessful" },
];

export const SupplierAwardCard: React.FC<SupplierAwardCardProps> = ({
  supplier,
  awardStatus,
  comment,
  isLeader,
  isBelowThreshold,
  showAwardControls,
  onAwardStatusChange,
  onCommentChange,
}) => {
  const displayScore =
    supplier.consensus_weighted_total ?? supplier.pre_meeting_weighted_average;

  return (
    <div
      className={cn(
        "rounded-lg border p-4 transition-opacity",
        isLeader
          ? "border-blue-300 bg-blue-50"
          : "border-gray-200 bg-white",
        isBelowThreshold && "opacity-60"
      )}
      data-testid={`supplier-award-card-${supplier.invite_id}`}
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-gray-900 truncate">
              {supplier.supplier_name}
            </h3>
            {isLeader && (
              <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                Top Scorer
              </span>
            )}
            {supplier.consensus_weighted_total === null && (
              <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                Pre-meeting avg
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right">
            <div className="text-xs text-gray-500 mb-0.5">Weighted Total</div>
            <div className="text-lg font-semibold tabular-nums text-gray-900">
              {formatScore(displayScore)}
            </div>
          </div>

          {supplier.diff !== null && supplier.consensus_weighted_total !== null && (
            <div className="text-right">
              <div className="text-xs text-gray-500 mb-0.5">vs Pre-meeting</div>
              <DiffDisplay diff={supplier.diff} />
            </div>
          )}
        </div>
      </div>

      {showAwardControls && (
        <div className="space-y-3 pt-3 border-t border-gray-200">
          <div>
            <p className="text-xs font-medium text-gray-600 mb-2">Award Decision</p>
            <div
              className="flex flex-wrap gap-3"
              role="radiogroup"
              aria-label={`Award decision for ${supplier.supplier_name}`}
            >
              {AWARD_STATUS_OPTIONS.map(({ value, label }) => (
                <label
                  key={value}
                  className="flex items-center gap-1.5 cursor-pointer select-none"
                >
                  <input
                    type="radio"
                    name={`award-status-${supplier.invite_id}`}
                    value={value}
                    checked={awardStatus === value}
                    onChange={() => onAwardStatusChange(supplier.invite_id, value)}
                    className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label
              htmlFor={`award-comment-${supplier.invite_id}`}
              className="block text-xs font-medium text-gray-600 mb-1"
            >
              Comment
              {(awardStatus === "awarded" || awardStatus === "unsuccessful") && (
                <span className="text-gray-400 font-normal ml-1">(optional)</span>
              )}
            </label>
            <textarea
              id={`award-comment-${supplier.invite_id}`}
              value={comment}
              onChange={(e) => onCommentChange(supplier.invite_id, e.target.value)}
              placeholder="Add rationale for this decision..."
              rows={2}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
            />
          </div>
        </div>
      )}
    </div>
  );
};
